import type {
  QuotaInfo,
  UserOrigin,
  UserSortDir,
  UserSortField,
  UserSummary,
} from "../api/types";
import { displayUserId } from "../lib/userId";

interface Props {
  users: UserSummary[];
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  sortBy: UserSortField;
  sortDir: UserSortDir;
  onSortChange: (sortBy: UserSortField, sortDir: UserSortDir) => void;
  onSelect: (user: UserSummary) => void;
  onPageChange: (nextOffset: number) => void;
  quotas?: Record<string, QuotaInfo>;
}

interface SortableHeaderProps {
  label: string;
  field: UserSortField;
  defaultDir: UserSortDir;
  sortBy: UserSortField;
  sortDir: UserSortDir;
  onSortChange: (sortBy: UserSortField, sortDir: UserSortDir) => void;
  className?: string;
}

function SortableHeader({
  label,
  field,
  defaultDir,
  sortBy,
  sortDir,
  onSortChange,
  className,
}: SortableHeaderProps): JSX.Element {
  const isActive = sortBy === field;
  const handleClick = (): void => {
    if (!isActive) {
      onSortChange(field, defaultDir);
      return;
    }
    onSortChange(field, sortDir === "asc" ? "desc" : "asc");
  };
  const arrow = isActive ? (sortDir === "asc" ? "▲" : "▼") : "";
  return (
    <th
      className={`sessions-table-th-sortable${isActive ? " is-sorted" : ""}${className ? ` ${className}` : ""}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      tabIndex={0}
      role="button"
      aria-sort={
        isActive ? (sortDir === "asc" ? "ascending" : "descending") : "none"
      }
    >
      {label}
      {arrow && <span className="sessions-table-sort-arrow"> {arrow}</span>}
    </th>
  );
}

const ORIGIN_LABEL: Record<UserOrigin, string> = {
  adobe: "Adobe",
  "non-adobe": "Non-Adobe",
  unknown: "Unknown",
};

const ORIGIN_COLORS: Record<UserOrigin, string> = {
  adobe: "#6366f1",
  "non-adobe": "#0ea5e9",
  unknown: "#71717a",
};

function formatTokens(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(value);
}

function formatCost(usd: number): string {
  if (usd >= 1000) return `$${(usd / 1000).toFixed(1)}k`;
  if (usd >= 1) return `$${usd.toFixed(2)}`;
  if (usd > 0) return `$${usd.toFixed(3)}`;
  return "$0";
}

function formatCompletion(rate: number | null): string {
  if (rate === null) return "—";
  return `${Math.round(rate * 100)}%`;
}

function formatLastSeen(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function modelBadgeLabel(model: string): string {
  // 'claude-sonnet-4-6' → 'sonnet 4.6', 'claude-sonnet-5' → 'sonnet 5'
  const m = /^claude-([a-z]+)-(\d+)(?:-(\d+))?$/i.exec(model);
  if (!m) return model;
  return m[3] ? `${m[1]} ${m[2]}.${m[3]}` : `${m[1]} ${m[2]}`;
}

function quotaStatus(q: QuotaInfo): "ok" | "warn" | "critical" {
  if (!q.allowed) return "critical";
  const pct = q.spentDollars / q.budgetDollars;
  if (pct >= 0.9) return "critical";
  if (pct >= 0.6) return "warn";
  return "ok";
}

const QUOTA_COLORS = { ok: "#22c55e", warn: "#f59e0b", critical: "#ef4444" };

export function UsersTable({
  users,
  total,
  limit,
  offset,
  loading,
  sortBy,
  sortDir,
  onSortChange,
  onSelect,
  onPageChange,
  quotas,
}: Props): JSX.Element {
  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + limit, total);
  const sortHeaderProps = { sortBy, sortDir, onSortChange };

  return (
    <div className="sessions-table-wrapper">
      <table className="sessions-table">
        <thead>
          <tr>
            <SortableHeader
              label="User"
              field="user_id"
              defaultDir="asc"
              {...sortHeaderProps}
            />
            <th>Origin</th>
            <SortableHeader
              label="Sessions"
              field="sessions"
              defaultDir="desc"
              {...sortHeaderProps}
            />
            <SortableHeader
              label="Tokens"
              field="total_tokens"
              defaultDir="desc"
              {...sortHeaderProps}
            />
            <SortableHeader
              label="Cost"
              field="total_cost"
              defaultDir="desc"
              {...sortHeaderProps}
            />
            <th>Quota (7d)</th>
            <SortableHeader
              label="Completion"
              field="completion_rate"
              defaultDir="desc"
              {...sortHeaderProps}
            />
            <th>Models</th>
            <SortableHeader
              label="Last seen"
              field="last_seen"
              defaultDir="desc"
              {...sortHeaderProps}
            />
          </tr>
        </thead>
        <tbody>
          {loading && users.length === 0 && (
            <tr>
              <td colSpan={9} className="sessions-table-loading">
                Loading users…
              </td>
            </tr>
          )}
          {!loading && users.length === 0 && (
            <tr>
              <td colSpan={9} className="sessions-table-empty">
                No users in this window.
              </td>
            </tr>
          )}
          {users.map((u) => (
            <tr
              key={u.userId}
              className="sessions-table-row"
              onClick={() => onSelect(u)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(u);
                }
              }}
            >
              <td className="sessions-table-user" title={u.userId}>
                {displayUserId(u.userId)}
              </td>
              <td>
                <span
                  className="badge badge-outcome"
                  style={{
                    backgroundColor: `${ORIGIN_COLORS[u.origin]}22`,
                    color: ORIGIN_COLORS[u.origin],
                  }}
                >
                  {ORIGIN_LABEL[u.origin]}
                </span>
              </td>
              <td className="sessions-table-events">
                {u.sessions.toLocaleString("en-US")}
              </td>
              <td
                className="sessions-table-events"
                title={u.totalTokens.toLocaleString("en-US")}
              >
                {formatTokens(u.totalTokens)}
              </td>
              <td className="sessions-table-events">
                {formatCost(u.totalCostUsd)}
              </td>
              <td className="sessions-table-events">
                {quotas?.[u.userId] ? (
                  (() => {
                    const q = quotas[u.userId]!;
                    const status = quotaStatus(q);
                    return (
                      <span
                        title={`Resets ${new Date(q.resetsAt).toLocaleDateString("en-US")}`}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: QUOTA_COLORS[status],
                            marginRight: 4,
                          }}
                        />
                        {formatCost(q.spentDollars)} /{" "}
                        {formatCost(q.budgetDollars)}
                      </span>
                    );
                  })()
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
              <td
                className="sessions-table-events"
                title={
                  u.completionRate === null
                    ? "No classified sessions yet"
                    : `${u.completedSessions} / ${u.classifiedSessions} classified`
                }
              >
                {formatCompletion(u.completionRate)}
              </td>
              <td>
                {u.models.length === 0 ? (
                  <span className="muted">—</span>
                ) : (
                  <span className="muted" style={{ fontSize: 12 }}>
                    {u.models.map(modelBadgeLabel).join(", ")}
                  </span>
                )}
              </td>
              <td className="sessions-table-time">
                {formatLastSeen(u.lastSeen)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {total > 0 && (
        <div className="sessions-pagination">
          <span>
            Showing {start}–{end} of {total.toLocaleString("en-US")}
          </span>
          <div className="sessions-pagination-buttons">
            <button
              type="button"
              disabled={offset === 0 || loading}
              onClick={() => onPageChange(Math.max(0, offset - limit))}
            >
              ← Prev
            </button>
            <button
              type="button"
              disabled={end >= total || loading}
              onClick={() => onPageChange(offset + limit)}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
